import type { SEOProps } from '~/hooks/useSEO';

export const SITE_NAME = '简创AIGC';
export const SITE_ORIGIN = 'https://jcaigc.cn';
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/logo256.png`;

/** 首页文案需与 index.html 保持一致，未执行 JS 的爬虫读的是 HTML。 */
export const HOME_TITLE = '简创AIGC - AI视频创作与剪映工作流';
export const HOME_DESCRIPTION =
  '简创AIGC提供 AI 视频创作、剪映小助手和可导入扣子的工作流模板，用来完成剪辑、特效与成片导出。';
export const HOME_KEYWORDS = '简创AIGC, AI视频创作, 剪映小助手, 扣子工作流';

export function pageUrl(path: string) {
  return `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

function clipText(text: string, max: number) {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
}

function pageSEO(
  path: string,
  title: string,
  description: string,
  keywords: string,
  robots = 'index, follow'
): SEOProps {
  return {
    title,
    description,
    keywords,
    robots,
    ogUrl: pageUrl(path),
    canonical: pageUrl(path),
  };
}

export const HOME_SEO: SEOProps = pageSEO('/', HOME_TITLE, HOME_DESCRIPTION, HOME_KEYWORDS);

export const WORKFLOW_SEO: SEOProps = pageSEO(
  '/workflow',
  '工作流商店 - 简创AIGC',
  '浏览可导入扣子空间的智能体与工作流模板，下载压缩包后可直接导入使用。',
  '工作流商店, 扣子工作流, 智能体模板, 简创AIGC'
);

export const MIGRATION_SEO: SEOProps = pageSEO(
  '/workflow/migration',
  '工作流迁移 - 简创AIGC',
  '把旧版插件工作流转换成新版插件格式，粘贴 JSON 即可得到可导入的工作流。',
  '工作流迁移, 扣子插件, 简创AIGC'
);

export const DOWNLOAD_SEO: SEOProps = pageSEO(
  '/download',
  '下载剪映小助手 - 简创AIGC',
  '开源剪映小助手支持草稿自动化与云渲染，提供 Windows 和 Mac 客户端，可配合扣子导出成片。',
  '剪映小助手, CapCut Mate, 剪映草稿, 简创AIGC'
);

export const RECHARGE_SEO: SEOProps = pageSEO(
  '/recharge',
  '会员与计费 - 简创AIGC',
  '了解简创AIGC会员权益、积分充值方式和插件计费标准。',
  '会员, 积分充值, 计费标准, 简创AIGC'
);

const PRIVATE = 'noindex, follow';

export const LOGIN_SEO: SEOProps = pageSEO(
  '/login',
  '登录 - 简创AIGC',
  '登录后可使用视频创作、工作流模板和剪映小助手。',
  '登录, 简创AIGC',
  PRIVATE
);

export const USER_INFO_SEO: SEOProps = pageSEO(
  '/user-info',
  '个人中心 - 简创AIGC',
  '查看和管理你的简创AIGC账户资料。',
  '个人中心, 简创AIGC',
  PRIVATE
);

export const UPLOAD_SEO: SEOProps = pageSEO(
  '/upload',
  '上传文件 - 简创AIGC',
  '上传视频、图片等素材，供后续创作使用。',
  '上传文件, 简创AIGC',
  PRIVATE
);

export const BALANCE_SEO: SEOProps = pageSEO(
  '/balance-log',
  '消费记录 - 简创AIGC',
  '查看账户充值与消费明细。',
  '消费记录, 简创AIGC',
  PRIVATE
);

export const CREATOR_SEO: SEOProps = pageSEO(
  '/workflow?menu=creator',
  '创作者中心 - 简创AIGC',
  '管理你已发布的智能体。',
  '创作者中心, 简创AIGC',
  PRIVATE
);

export const PURCHASE_SEO: SEOProps = pageSEO(
  '/workflow?menu=record',
  '购买记录 - 简创AIGC',
  '查看你已购买的工作流。',
  '购买记录, 简创AIGC',
  PRIVATE
);

export const CUSTOM_SEO: SEOProps = pageSEO(
  '/workflow?menu=custom',
  '定制服务 - 简创AIGC',
  '查看你提交的定制需求。',
  '定制服务, 简创AIGC',
  PRIVATE
);

export const NOT_FOUND_SEO: SEOProps = pageSEO(
  '/404',
  '页面未找到 - 简创AIGC',
  '这个页面不存在，可以返回首页继续浏览。',
  '页面未找到, 简创AIGC',
  PRIVATE
);

export const ERROR_SEO: SEOProps = pageSEO(
  '/error',
  '访问出错 - 简创AIGC',
  '页面暂时无法打开，请稍后重试或返回首页。',
  '访问出错, 简创AIGC',
  PRIVATE
);

export const EXTERNAL_FEATURES_SEO: SEOProps = pageSEO(
  '/external-features',
  '剪映小助手 - 简创AIGC',
  '剪映小助手内的说明页，用于草稿下载与版本提示。',
  '剪映小助手, 简创AIGC',
  PRIVATE
);

export function buildBotSEO({
  name,
  description,
  id,
  image,
}: {
  name?: string;
  description?: string;
  id: string;
  image?: string;
}): SEOProps {
  const rawName = (name || '').replace(/\s+/g, ' ').trim();
  const titleName = rawName || '智能体详情';
  const shortName = titleName.length > 18 ? `${titleName.slice(0, 17)}…` : titleName;
  const summary = clipText(description || '', 90);

  return {
    title: `${shortName} - 简创AIGC`,
    description: summary || '查看该智能体的介绍、包含的工作流和使用说明。',
    keywords:
      rawName && rawName.length <= 20
        ? `${rawName}, 工作流模板, 简创AIGC`
        : '工作流模板, 智能体, 简创AIGC',
    robots: 'index, follow',
    ogUrl: pageUrl(`/workflow/bot/${id}`),
    canonical: pageUrl(`/workflow/bot/${id}`),
    ogImage: image && /^https?:\/\//.test(image) ? image : undefined,
  };
}
